import React from "react";
import { mealTypes } from "../../../enums";
import { getRoomPricesWithDetails } from "../../../firestore/firestoreService";
import { buildPriceLabels } from "./hotelItemHelpers";

interface ISuggestionBoxProps {
  dateRange: string;
  startDate: string;
  endDate: string;
  hotelId: string;
  adults: number;
  children: number;
  infants: number;
  partnerId?: string;
  roomName?: string;
  meta: string;
  priceLabel: string;
  status: "on-request" | "available";
  onClick?: () => void;
}

export const SuggestionBox: React.FC<ISuggestionBoxProps> = ({
  dateRange,
  startDate,
  endDate,
  hotelId,
  adults,
  children,
  infants,
  partnerId,
  roomName,
  meta,
  priceLabel,
  status,
  onClick,
}) => {
  const [resolvedPriceLabel, setResolvedPriceLabel] =
    React.useState(priceLabel);
  const [resolvedDiscountPriceLabel, setResolvedDiscountPriceLabel] =
    React.useState("");
  const [resolvedHasDiscountPrice, setResolvedHasDiscountPrice] =
    React.useState(false);
  const [resolvedRoomName, setResolvedRoomName] = React.useState(
    roomName || "",
  );
  const [resolvedMealsType, setResolvedMealsType] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);
  const [resolvedStatus, setResolvedStatus] = React.useState(status);

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      if (!hotelId || !startDate || !endDate) return;
      setIsLoading(true);
      try {
        const data = await getRoomPricesWithDetails({
          hotelId,
          fromDate: startDate,
          toDate: endDate,
          adults,
          children,
          infants,
          partnerId,
        });
        const hotelData = data.find((record) => record.id === hotelId);
        const room = hotelData?.rooms?.[0];
        const nextPriceLabels = buildPriceLabels(
          room?.totalPrice,
          room?.discountPrice,
        );
        if (!active) return;
        setResolvedPriceLabel(
          nextPriceLabels.priceLabel !== "—"
            ? nextPriceLabels.priceLabel
            : priceLabel,
        );
        setResolvedDiscountPriceLabel(nextPriceLabels.discountPriceLabel);
        setResolvedHasDiscountPrice(nextPriceLabels.hasDiscountPrice);
        setResolvedRoomName(room?.name || roomName || "");
        const rawMealsType = room?.mealsType || "";
        setResolvedMealsType(
          rawMealsType && mealTypes[rawMealsType]?.label
            ? mealTypes[rawMealsType].label
            : rawMealsType,
        );
        const hasAvailability =
          (room?.availableRoomsCount || 0) > 0 &&
          !room?.anyStopSale &&
          !room?.allPricesStopSale;
        const nextStatus = hasAvailability ? "available" : "on-request";
        setResolvedStatus(nextStatus);
        setIsLoading(false);
      } catch (error) {
        if (active) {
          setResolvedPriceLabel(priceLabel);
          setResolvedDiscountPriceLabel("");
          setResolvedHasDiscountPrice(false);
          setResolvedRoomName(roomName || "");
          setResolvedMealsType("");
          setResolvedStatus(status);
          setIsLoading(false);
        }
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    load();
    return () => {
      active = false;
    };
  }, [
    hotelId,
    startDate,
    endDate,
    adults,
    children,
    infants,
    partnerId,
    priceLabel,
    roomName,
    status,
  ]);
  const showDiscountPrice =
    resolvedHasDiscountPrice &&
    resolvedDiscountPriceLabel !== resolvedPriceLabel;

  return (
    <div
      className="hotel-item-suggestion-box"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="suggestion-dates">{dateRange}</div>
      {resolvedRoomName && (
        <div className="suggestion-room">{resolvedRoomName}</div>
      )}
      {resolvedMealsType && (
        <div className="suggestion-meals">{resolvedMealsType}</div>
      )}
      <div className="suggestion-meta">{meta}</div>
      <div className="suggestion-price">
        {isLoading ? (
          <span className="suggestion-loading">Φόρτωση...</span>
        ) : (
          <div className="suggestion-price-row">
            <span>Από</span>
            <div className="price-amounts">
              {showDiscountPrice && (
                <div className="price-amount selling">
                  {resolvedDiscountPriceLabel}
                </div>
              )}
              <div className="price-amount">{resolvedPriceLabel}</div>
            </div>
          </div>
        )}
      </div>
      <div
        className={`suggestion-status ${
          resolvedStatus === "on-request" ? "on-request" : "available"
        }`}
      >
        {resolvedStatus === "on-request" ? "On Request" : "Διαθέσιμο"}
      </div>
    </div>
  );
};
